import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

interface SliderPopoverProps {
    icon: LucideIcon;
    value: number;
    onValueChange: (value: number) => void;
    min: number;
    max: number;
    step: number;
    label: string;
    formatValue: (value: number) => string;
    minLabel?: string;
    maxLabel?: string;
    description?: string;
    onOpenChange?: (open: boolean) => void;
    forceClose?: boolean;
    compact?: boolean;
    showIcon?: boolean;
    className?: string;
}

export function SliderPopover({
    icon: Icon,
    value,
    onValueChange,
    min,
    max,
    step,
    label,
    formatValue,
    minLabel,
    maxLabel,
    description,
    onOpenChange,
    forceClose,
    compact = false,
    showIcon = true,
    className
}: SliderPopoverProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const closeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // Handle force close from parent
    useEffect(() => {
        if (forceClose && isOpen) {
            // Instant close without animation
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            setIsOpen(false);
            setIsClosing(false);
            onOpenChange?.(false);
        }
    }, [forceClose, isOpen, onOpenChange]);

    // Handle click outside to close popover
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isOpen && 
                popoverRef.current && 
                !popoverRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)) {
                closePopover();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen]);

    const handleClick = () => {
        // Clear any pending close timeout
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = undefined;
        }
        
        if (isOpen) {
            closePopover();
        } else {
            setIsClosing(false);
            setIsOpen(true);
            onOpenChange?.(true);
        }
    };


    const closePopover = () => {
        setIsClosing(true);
        closeTimeoutRef.current = setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
            onOpenChange?.(false);
        }, 100); // Reduced from 150ms to 100ms for snappier response
    };

    return (
        <div className={cn("relative", compact ? "px-0" : "px-3")}>
            <Button
                ref={buttonRef}
                variant="secondary"
                size={compact ? "sm" : "sm"}
                className={cn(
                    compact 
                        ? "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground px-2 py-1 h-auto text-xs cursor-pointer"
                        : "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer",
                    className
                )}
                onClick={handleClick}
            >
                {showIcon && <Icon size={compact ? 12 : 16} className={compact ? "mr-1" : "mr-2"} />}
                <span>{formatValue(value)}</span>
            </Button>

            {isOpen && (
                <Card
                    ref={popoverRef}
                    className={cn(
                        "absolute bottom-full left-0 mb-3 shadow-xl w-80 z-50 border-border/50 bg-popover",
                        isClosing
                            ? "animate-out fade-out-0 zoom-out-95 slide-out-to-bottom-2"
                            : "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2",
                        "duration-100" // Reduced from 150ms to 100ms
                    )}
                >
                    <CardContent className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <span className="text-sm font-medium text-foreground">{label}</span>
                            <span className="text-sm text-muted-foreground">{formatValue(value)}</span>
                        </div>

                        <div className="relative">
                            <Slider
                                value={[value]}
                                onValueChange={(values) => onValueChange(values[0])}
                                min={min}
                                max={max}
                                step={step}
                                className="w-full"
                            />
                        </div>

                        {(minLabel || maxLabel) && (
                            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                                <span>{minLabel || `${min}`}</span>
                                <span>{maxLabel || `${max}`}</span>
                            </div>
                        )}

                        {description && (
                            <p className="mt-4 text-xs text-muted-foreground">
                                {description}
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}